import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const steps = [
  {
    number: "01",
    title: "Missed Call Detected",
    description: "When you can't answer, our AI instantly picks up the lead. No more voicemails that never get returned.",
  },
  {
    number: "02",
    title: "AI Responds in Seconds",
    description: "A personalized text goes out within 5 seconds. The customer feels heard before they can even call your competitor.",
  },
  {
    number: "03",
    title: "Lead Qualified & Booked",
    description: "AI qualifies the job type, confirms the address, and books them into your calendar. Zero back-and-forth.",
  },
  {
    number: "04",
    title: "Review Request Sent",
    description: "After the job, an automated follow-up asks for a 5-star Google review. Your online reputation grows on autopilot.",
  },
];

export default function HowItWorks() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative py-24 md:py-32 px-6 md:px-12"
      style={{ backgroundColor: "#0D0D0D" }}
    >
      <div className="max-w-7xl mx-auto" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-20"
        >
          <span
            className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[#5B8DEF] font-medium mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="how-it-works-label"
          >
            <span className="w-6 h-px bg-[#5B8DEF]"></span>
            How It Works
            <span className="w-6 h-px bg-[#5B8DEF]"></span>
          </span>
          <h2
            className="text-3xl md:text-4xl tracking-tight font-semibold text-white"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="how-it-works-headline"
          >
            Four Steps. Fully Automated.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-4 gap-0">
          {steps.map((step, i) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.12 * (i + 1) }}
              data-testid={`step-${i}`}
              className="relative px-6 py-8"
            >
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div className="hidden md:block absolute top-[52px] right-0 w-full h-px bg-white/[0.06]" style={{ left: "50%" }}></div>
              )}

              <div className="relative">
                <span
                  className="block text-5xl font-bold text-white/[0.06] mb-4"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {step.number}
                </span>
                <h3
                  className="text-lg font-semibold text-white mb-3"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-sm text-[#A3A3A3] leading-relaxed"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
